import HistoryIcon from '@mui/icons-material/History';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Box, IconButton, InputAdornment, TextField, Tooltip } from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from '../../redux/store';
import { useTabs } from '../../context/TabContext';
import { useUpdateOrderItemRedux } from '../../hooks/useUpdateOrderItemRedux';
import { useScopeCheck } from '../scopes';

QuantityField.propTypes = {
  row: PropTypes.object,
  disabled: PropTypes.bool
};

export default function QuantityField({ row, disabled = false }) {
  const hasAccess = useScopeCheck(['Edit-Order'], true);
  const { id = '' } = useParams();
  const { orderById } = useSelector((state) => state.orders || {});
  const { items = [], orderAgoraCategories = [] } = orderById[id] || {};
  const permittedCategoryIds = new Set(orderAgoraCategories.map((category) => category.id));
  const canEditCategories = hasAccess && items.every(
    (item) => item.agoraCategory?.id === 0 || permittedCategoryIds.has(item.agoraCategory?.id)
  );
  const isDisabled = row.punchoutOrderLocked || !canEditCategories || disabled;
  const updateOrderItemRedux = useUpdateOrderItemRedux();
  const [qty, setQty] = useState(row.quantity);

  useEffect(() => {
    setQty(row.quantity)
  }, [row]);

  const { activeTab } = useTabs();

  const showInputAdornment = row?.origQty !== undefined && row?.origQty !== row.quantity;

  return <Tooltip title={row.punchoutOrderLocked ? "The vendor doesn't allow changes to punchout orders, to make changes, you must place a new order." :
    !canEditCategories ? "Permission Required" : ""} arrow>
    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
      <TextField
        disabled={isDisabled}
        sx={{
          '& .MuiInputBase-input.Mui-disabled': {
            WebkitTextFillColor: 'currentColor',
            color: 'text.primary',
          },
          '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': {
            borderColor: '#637381',
          },
        }}
        size="small"
        value={qty || ''}
        id={`${activeTab.key}quantity-${row.id}`}
        autoComplete="off"
        onChange={(e) => {
          const newValue = Number(e.target.value);
          if (!Number.isNaN(newValue) && newValue >= 0) {
            setQty(newValue);
            if (row?.origQty === undefined) {
              updateOrderItemRedux(row.id, 'origQty', row.quantity);
            }
            updateOrderItemRedux(row.id, 'quantity', newValue);
          }
        }}
        InputProps={{
          style: { height: '30px', borderRadius: '4px' },
          startAdornment: showInputAdornment && (
            <InputAdornment position="start">
              {row?.origQty !== undefined && row?.origQty !== row.quantity && (
                <Tooltip title={row?.origQty} placement="top" disableHoverListener={disabled}>
                  <span>
                    <IconButton
                      onClick={() => {
                        updateOrderItemRedux(row.id, 'quantity', row?.origQty);
                        setQty(row?.origQty);
                      }}
                      sx={{
                        padding: '0px',
                        margin: '0px',
                        height: '15px',
                        ml: '-5px',
                        width: '15px',
                      }}
                      color=""
                      disabled={disabled}
                    >
                      <RefreshIcon color={disabled ? 'disabled' : 'inherit'} />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </InputAdornment>
          ),
        }}
      />
      {row?.originalQuantity !== undefined &&
       row?.originalQuantity !== row?.origQty &&
       row?.originalQuantity !== row.quantity && (
          <Tooltip title={`Revert back to ${row?.originalQuantity}`} placement="top" arrow disableHoverListener={disabled}>
            <span>
              <IconButton
                onClick={() => {
                  updateOrderItemRedux(row.id, 'quantity', row?.originalQuantity);
                  setQty(row?.originalQuantity);
                }}
                sx={{ padding: '0px', margin: '0px', ml: '2px' }}
                color={row.quantity === row?.originalQuantity ? '' : 'secondary'}
                disabled={disabled}
              >
                <HistoryIcon color={disabled ? 'disabled' : 'secondary'} />
              </IconButton>
            </span>
          </Tooltip>
        )}
    </Box>
  </Tooltip>;
}
